import { useState } from 'react'
import type { AuditEntryDto } from '@lunary/api-client'
import { useAdminAuth } from '../../shared/auth/admin-auth-context'
import { EntryRow } from './entry-row'
import { AuditFilters, EMPTY_FILTER } from './filters'
import type { AuditFilter } from './filters'
import { PAGE_SIZE, useAuditLog } from './queries'
import styles from './audit-log.module.css'

const DAY = new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', day: 'numeric', month: 'long', weekday: 'long' })

function groupByDay(items: readonly AuditEntryDto[]): Array<{ day: string; items: AuditEntryDto[] }> {
  const groups: Array<{ day: string; items: AuditEntryDto[] }> = []
  for (const item of items) {
    const day = DAY.format(new Date(item.created_at))
    const last = groups.at(-1)
    if (last && last.day === day) last.items.push(item)
    else groups.push({ day, items: [item] })
  }
  return groups
}

export function AuditLogPage() {
  const { user, status } = useAdminAuth()
  const [filter, setFilter] = useState<AuditFilter>(EMPTY_FILTER)
  const [offset, setOffset] = useState(0)
  const [openId, setOpenId] = useState<string | null>(null)
  const isAdmin = status === 'authenticated' && user?.role === 'admin'
  const list = useAuditLog(filter, offset, isAdmin)

  if (!isAdmin) return <p className="error-box">Раздел доступен только администратору.</p>

  const total = list.data?.total ?? 0
  const groups = groupByDay(list.data?.items ?? [])

  return (
    <div>
      <h1>Журнал действий</h1>
      <p className="muted">
        Изменения в админке и ошибки запросов. Чтения и входы не записываются. Записи хранятся 12 месяцев и не редактируются.
      </p>

      <AuditFilters
        value={filter}
        actors={list.data?.actors ?? []}
        onChange={(next) => {
          setFilter(next)
          setOffset(0)
        }}
      />

      {list.isPending && <div className="spinner" aria-label="Загрузка" />}
      {list.isError && <p className="error-box">Не удалось загрузить журнал.</p>}

      {list.data && (
        <div className="card-panel">
          {groups.map((g) => (
            <section key={g.day} aria-label={g.day}>
              <h2 className={styles.day}>{g.day}</h2>
              {g.items.map((item) => (
                <EntryRow
                  key={item.id}
                  entry={item}
                  open={openId === item.id}
                  onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                />
              ))}
            </section>
          ))}
          {list.data.items.length === 0 && (
            <p className="muted">
              {filter === EMPTY_FILTER ? 'Пока пусто: здесь появятся изменения в админке и ошибки запросов.' : 'Ничего не нашлось.'}
            </p>
          )}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="row">
          <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Назад
          </button>
          <span className="muted">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}
          </span>
          <button className="secondary" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Дальше
          </button>
        </div>
      )}
    </div>
  )
}
